/* ==========================================================================
   FILE: modules/brain/components/GuardrailSettings.jsx
   IAMONEAI - Guardrail Settings Component
   Domain restrictions and safety boundaries configuration
   ========================================================================== */

import React, { useState, useCallback } from 'react';
import { Toggle, Button, Input } from '../../../global/components';
import './GuardrailSettings.css';

// ==========================================================================
// DOMAIN DEFINITIONS
// ==========================================================================

const AVAILABLE_DOMAINS = [
  {
    id: 'chat',
    label: 'General Chat',
    description: 'Casual conversation and questions',
    icon: '💬',
    category: 'core',
  },
  {
    id: 'memory',
    label: 'Memory',
    description: 'Remember things about you',
    icon: '🧠',
    category: 'core',
  },
  {
    id: 'knowledge',
    label: 'Knowledge',
    description: 'Store facts and relationships',
    icon: '📚',
    category: 'core',
  },
  {
    id: 'productivity',
    label: 'Productivity',
    description: 'Task management and planning',
    icon: '✅',
    category: 'work',
  },
  {
    id: 'learning',
    label: 'Learning',
    description: 'Education and skill development',
    icon: '📖',
    category: 'work',
  },
  {
    id: 'career',
    label: 'Career',
    description: 'Job advice and professional growth',
    icon: '💼',
    category: 'work',
  },
  {
    id: 'finance',
    label: 'Finance',
    description: 'Money, investments, budgeting',
    icon: '💰',
    category: 'sensitive',
    warning: 'I won\'t give specific financial advice, but can discuss general concepts',
  },
  {
    id: 'medical',
    label: 'Medical',
    description: 'Health questions and concerns',
    icon: '🏥',
    category: 'sensitive',
    warning: 'I won\'t diagnose or prescribe, but can provide general health info',
  },
  {
    id: 'legal',
    label: 'Legal',
    description: 'Legal questions and guidance',
    icon: '⚖️',
    category: 'sensitive',
    warning: 'I won\'t provide legal advice, but can explain general concepts',
  },
  {
    id: 'relationships',
    label: 'Relationships',
    description: 'Dating and relationship advice',
    icon: '❤️',
    category: 'personal',
  },
  {
    id: 'mental_health',
    label: 'Mental Health',
    description: 'Emotional support and guidance',
    icon: '🧘',
    category: 'personal',
  },
  {
    id: 'creative',
    label: 'Creative',
    description: 'Writing, art, and creative projects',
    icon: '🎨',
    category: 'creative',
  },
  {
    id: 'coding',
    label: 'Coding',
    description: 'Programming and technical help',
    icon: '💻',
    category: 'technical',
  },
];

const DOMAIN_CATEGORIES = {
  core: { label: 'Core Features', description: 'Essential functionality' },
  work: { label: 'Work & Learning', description: 'Professional development' },
  sensitive: { label: 'Sensitive Topics', description: 'Requires careful handling' },
  personal: { label: 'Personal Life', description: 'Private matters' },
  creative: { label: 'Creative', description: 'Artistic endeavors' },
  technical: { label: 'Technical', description: 'Development and tech' },
};

// ==========================================================================
// GUARDRAIL SETTINGS COMPONENT
// ==========================================================================

/**
 * Guardrail configuration for domain restrictions
 * 
 * @param {Object} props
 * @param {Object} guardrails - Current guardrail settings
 * @param {Function} onUpdate - Handler for updates
 */
function GuardrailSettings({
  guardrails = {},
  onUpdate,
  className = '',
}) {
  const [customDomain, setCustomDomain] = useState('');

  // Current settings with defaults
  const allowedDomains = guardrails.allowedDomains || ['chat', 'memory', 'knowledge'];
  const restrictedDomains = guardrails.restrictedDomains || [];
  const riskTolerance = guardrails.riskTolerance || 'low';

  // ==========================================================================
  // HANDLERS
  // ==========================================================================

  const isDomainAllowed = (domainId) => {
    return allowedDomains.includes(domainId) && !restrictedDomains.includes(domainId);
  };

  const handleDomainToggle = useCallback((domainId, enabled) => {
    if (enabled) {
      // Add to allowed, remove from restricted
      const newAllowed = [...new Set([...allowedDomains, domainId])];
      const newRestricted = restrictedDomains.filter(d => d !== domainId);
      onUpdate?.('guardrails.allowedDomains', newAllowed);
      onUpdate?.('guardrails.restrictedDomains', newRestricted);
    } else {
      // Remove from allowed, add to restricted
      const newAllowed = allowedDomains.filter(d => d !== domainId);
      const newRestricted = [...new Set([...restrictedDomains, domainId])];
      onUpdate?.('guardrails.allowedDomains', newAllowed);
      onUpdate?.('guardrails.restrictedDomains', newRestricted);
    }
  }, [allowedDomains, restrictedDomains, onUpdate]);

  const handleAddCustomDomain = useCallback(() => {
    const trimmed = customDomain.trim().toLowerCase();
    if (trimmed && !allowedDomains.includes(trimmed) && !restrictedDomains.includes(trimmed)) {
      onUpdate?.('guardrails.restrictedDomains', [...restrictedDomains, trimmed]);
      setCustomDomain('');
    }
  }, [customDomain, allowedDomains, restrictedDomains, onUpdate]);

  const handleRemoveCustomDomain = useCallback((domain) => {
    onUpdate?.('guardrails.restrictedDomains', restrictedDomains.filter(d => d !== domain));
  }, [restrictedDomains, onUpdate]);

  const handleRiskToleranceChange = useCallback((level) => {
    onUpdate?.('guardrails.riskTolerance', level);
  }, [onUpdate]);

  // ==========================================================================
  // RENDER
  // ==========================================================================

  // Group domains by category
  const domainsByCategory = AVAILABLE_DOMAINS.reduce((acc, domain) => {
    if (!acc[domain.category]) {
      acc[domain.category] = [];
    }
    acc[domain.category].push(domain);
    return acc;
  }, {});

  // Get custom restricted domains (not in predefined list)
  const predefinedIds = AVAILABLE_DOMAINS.map(d => d.id);
  const customRestricted = restrictedDomains.filter(d => !predefinedIds.includes(d));

  const settingsClasses = [
    'guardrail-settings',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={settingsClasses}>
      {/* Introduction */}
      <div className="guardrail-intro">
        <div className="guardrail-intro-icon">🛡️</div>
        <div className="guardrail-intro-content">
          <h4>Your Boundaries, Your Rules</h4>
          <p>
            Control which topics I can help with. Restricted topics will receive 
            a caring refusal that respects your boundaries.
          </p>
        </div>
      </div>

      {/* Risk Tolerance */}
      <div className="settings-section">
        <div className="settings-section-header">
          <h3 className="settings-section-title">Risk Tolerance</h3>
          <p className="settings-section-desc">
            How cautious should I be with sensitive topics?
          </p>
        </div>

        <div className="risk-tolerance-options">
          {['low', 'medium', 'high'].map((level) => (
            <button
              key={level}
              className={`risk-option ${riskTolerance === level ? 'risk-option-active' : ''}`}
              onClick={() => handleRiskToleranceChange(level)}
            >
              <span className="risk-option-icon">
                {level === 'low' && '🔒'}
                {level === 'medium' && '⚖️'}
                {level === 'high' && '🔓'}
              </span>
              <span className="risk-option-label">{level.charAt(0).toUpperCase() + level.slice(1)}</span>
              <span className="risk-option-desc">
                {level === 'low' && 'Extra careful'}
                {level === 'medium' && 'Balanced'}
                {level === 'high' && 'More flexible'}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Domain Categories */}
      <div className="settings-section">
        <div className="settings-section-header">
          <h3 className="settings-section-title">Topics & Domains</h3>
          <p className="settings-section-desc">
            Enable or disable specific topics
          </p>
        </div>

        <div className="domain-categories">
          {Object.entries(domainsByCategory).map(([categoryId, domains]) => {
            const category = DOMAIN_CATEGORIES[categoryId];
            return (
              <div key={categoryId} className="domain-category">
                <div className="domain-category-header">
                  <h4 className="domain-category-title">{category.label}</h4>
                  <span className="domain-category-desc">{category.description}</span>
                </div>

                <div className="domain-list">
                  {domains.map((domain) => (
                    <DomainToggle
                      key={domain.id}
                      domain={domain}
                      enabled={isDomainAllowed(domain.id)}
                      onToggle={(enabled) => handleDomainToggle(domain.id, enabled)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Custom Restrictions */}
      <div className="settings-section">
        <div className="settings-section-header">
          <h3 className="settings-section-title">Custom Restrictions</h3>
          <p className="settings-section-desc">
            Add specific topics you want me to avoid
          </p>
        </div>

        <div className="custom-restrictions">
          <div className="custom-restriction-input">
            <Input
              type="text"
              placeholder="e.g., politics, religion, stocks"
              value={customDomain}
              onChange={(e) => setCustomDomain(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCustomDomain();
                }
              }}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={handleAddCustomDomain}
              disabled={!customDomain.trim()}
            >
              Add
            </Button>
          </div>

          {customRestricted.length > 0 && (
            <div className="custom-restriction-list">
              {customRestricted.map((domain) => (
                <div key={domain} className="custom-restriction-tag">
                  <span>🚫 {domain}</span>
                  <button
                    onClick={() => handleRemoveCustomDomain(domain)}
                    aria-label={`Remove ${domain}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Currently Restricted Summary */}
      {restrictedDomains.length > 0 && (
        <div className="restricted-summary">
          <h4>Currently Restricted</h4>
          <p>
            I will politely decline to help with: {' '}
            <strong>{restrictedDomains.join(', ')}</strong>
          </p>
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// DOMAIN TOGGLE COMPONENT
// ==========================================================================

/**
 * Individual domain toggle with icon and description
 */
function DomainToggle({ domain, enabled, onToggle }) {
  return (
    <div className={`domain-toggle ${enabled ? 'domain-toggle-enabled' : 'domain-toggle-disabled'}`}>
      <div className="domain-toggle-content">
        <span className="domain-toggle-icon">{domain.icon}</span>
        <div className="domain-toggle-info">
          <span className="domain-toggle-label">{domain.label}</span>
          <span className="domain-toggle-desc">{domain.description}</span>
          {domain.warning && !enabled && (
            <span className="domain-toggle-warning">
              ⚠️ {domain.warning}
            </span>
          )}
        </div>
      </div>
      <Toggle
        checked={enabled}
        onChange={onToggle}
        aria-label={`${enabled ? 'Disable' : 'Enable'} ${domain.label}`}
      />
    </div>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default GuardrailSettings;